import {useState} from 'react';
import MinusIcon from '@/icons/zoom-out.svg';
import PlusIcon from '@/icons/zoom-in.svg';
import DropdownArrow from '@/icons/dropdown-arrow-new.svg';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@features/ui/components/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
} from '@features/ui/components/dropdown-menu';
import {cn} from '@features/ui/utils';

type ZoomControlsProps = {
  scale: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomToFit: () => void;
  onZoomToPercentage: (percentage: number) => void;
  onZoomToSelected?: () => void;
  colorClassName?: string;
};

export function ZoomControls({
  scale,
  onZoomIn,
  onZoomOut,
  onZoomToFit,
  onZoomToPercentage,
  onZoomToSelected,
  colorClassName = 'text-muted-foreground',
}: ZoomControlsProps) {
  return (
    <div className='flex h-8 flex-row items-center gap-0'>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            aria-label='Zoom out'
            className={cn(
              '-mr-0.5 flex h-full items-center justify-center',
              colorClassName,
            )}
            onClick={onZoomOut}
            onKeyDown={(e) => {
              if (e.key === ' ') e.preventDefault();
            }}
          >
            <MinusIcon />
          </button>
        </TooltipTrigger>
        <TooltipContent sideOffset={8}>Zoom out</TooltipContent>
      </Tooltip>
      <ZoomScale
        scale={scale}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        onZoomToFit={onZoomToFit}
        onZoomToPercentage={onZoomToPercentage}
        onZoomToSelected={onZoomToSelected}
        colorClassName={colorClassName}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            aria-label='Zoom in'
            className={cn(
              '-ml-0.5 flex h-full items-center justify-center',
              colorClassName,
            )}
            onClick={onZoomIn}
            onKeyDown={(e) => {
              if (e.key === ' ') e.preventDefault();
            }}
          >
            <PlusIcon />
          </button>
        </TooltipTrigger>
        <TooltipContent sideOffset={8}>Zoom in</TooltipContent>
      </Tooltip>
    </div>
  );
}

function ZoomScale({
  scale,
  onZoomIn,
  onZoomOut,
  onZoomToFit,
  onZoomToPercentage,
  onZoomToSelected,
  colorClassName,
}: ZoomControlsProps & {colorClassName: string}) {
  const [customZoom, setCustomZoom] = useState('');
  const [open, setOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) setCustomZoom('');
  };

  const handleCustomZoomSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const value = parseFloat(customZoom.replace('%', ''));
    if (!isNaN(value) && value > 0) {
      onZoomToPercentage(value);
      setOpen(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center justify-center text-[14px] font-semibold outline-none',
            colorClassName,
          )}
        >
          <span className='w-[40px] text-center tabular-nums'>
            {Math.round(scale * 100)}%
          </span>
          <DropdownArrow className='-ml-0.5 size-4 opacity-50' />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='center' className='w-48'>
        <div className='pb-1'>
          <form onSubmit={handleCustomZoomSubmit}>
            <input
              type='text'
              value={customZoom || `${Math.round(scale * 100)}%`}
              onChange={(e) => setCustomZoom(e.target.value)}
              onFocus={(e) => e.target.select()}
              className='h-8 w-full rounded-lg bg-white/10 px-3 text-sm font-semibold tabular-nums text-white focus:outline-none'
            />
          </form>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onZoomIn}>
          Zoom in
          <DropdownMenuShortcut>+</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onZoomOut}>
          Zoom out
          <DropdownMenuShortcut>−</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            onZoomToFit();
            setOpen(false);
          }}
        >
          Zoom to fit
        </DropdownMenuItem>
        {onZoomToSelected && (
          <DropdownMenuItem
            onClick={() => {
              onZoomToSelected();
              setOpen(false);
            }}
          >
            Zoom to selected
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            onZoomToPercentage(50);
            setOpen(false);
          }}
        >
          50%
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            onZoomToPercentage(100);
            setOpen(false);
          }}
        >
          100%
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            onZoomToPercentage(200);
            setOpen(false);
          }}
        >
          200%
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
